import { Component, Inject, Injector, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { CoreGridComponent } from '../../shared/grid/core-grid/core-grid.component';
import { SampleSurvey } from '../../models/sampleSurvey.model';
import { ToastService } from '../../services/toast.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommonService } from '../../services/common.service';
import { ValueConversionService } from '../../services/value-conversion.service';
import { AuthService } from '../../services';
import { SampleSurveyService } from '../../services/sample-survey.service';
import { DeleteDialog } from '../../shared/delete-dialog/delete-dialog.component';
import { ColumnConfig } from '../../shared/grid/core-grid/columnConfig.model';
import nameOf from '../../shared/form/nameOf.helper';
import { forkJoin, Observable, of } from 'rxjs';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { SurveyDialogComponent } from 'src/app/modals/survey-dialog/survey-dialog.component';
import { SampleSurveyTypeService } from 'src/app/services/sample-survey-type.service';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from '../../shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { Router } from '@angular/router';
import { sampleRouteConfig } from 'src/app/shared/constants/sampleRouteConfig.constants';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { TranslateService } from 'src/app/services/translate.service';
import { SurveyStatusLabels, SurveyStatusCssClasses } from 'src/app/enums/surveyStatus.enum';

@Component({
  standalone: false,
  selector: 'app-survey-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './survey-list.component.scss']
})
export class SurveyListComponent extends CoreGridComponent<SampleSurvey> implements OnInit {
  sampleSurveyTypes: Lookup[] = [];
  organizationLevels: Lookup[] = [];

  constructor(
    protected gridService: SampleSurveyService,
    protected toastService: ToastService,
    protected dialog: MatDialog,
    protected commonService: CommonService,
    protected valueConversionService: ValueConversionService,
    private sampleSurveyTypeService: SampleSurveyTypeService,
    private organizationLevelService: OrganizationLevelService,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private dateFormatService: DateFormatService,
    private router: Router,
    private translateService: TranslateService,
    private injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<SurveyListComponent>, null);
    super.ngOnInit();
    this.loadHeaderData();
    forkJoin({
      organizationLevels: this.organizationLevelService.getAll(),
      sampleSurveyTypes: this.sampleSurveyTypeService.getAll()
    }).subscribe({
      next: res => {
        this.sampleSurveyTypes = res.sampleSurveyTypes;
        this.organizationLevels = res.organizationLevels;
      },
      error: error => {
        this.toastService.showError('Failed to load lookup data');
      }
    });
  }

  override initializeColumns(): void {
    this.additionalActions = [];
    this.columnConfigs = [
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.name),
        displayName: 'Survey name',
        cssClass: () => 'cursor-pointer',
        cellTemplate: (survey) => survey.name,
        onClick: (survey) => this.openSampleSurveyDetail(survey)
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.surveyTypeId),
        displayName: 'Survey Type',
        cellTemplate: (sampleSurvey) => this.sampleSurveyTypes.find(res => res.id === sampleSurvey.surveyTypeId)?.name ?? '-'
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.surveyCode),
        displayName: 'Survey Code',
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.surveyDescription),
        displayName: 'Survey Description',
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.requestedById),
        displayName: 'Requested By',
        cellTemplate: (survey) => survey.endPoint?.name ?? survey.requestedById ?? '-'
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.surveyPlannedStartDate),
        displayName: 'Start Date',
        cellTemplate: (survey) => survey.surveyPlannedStartDate ?
          this.dateFormatService.convertUTCDateStringToUserTimeZoneString(survey.surveyPlannedStartDate, this.dateFormatService.userDateFormat) : '-'
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.surveyPlannedEndDate),
        displayName: 'End Date',
        cellTemplate: (survey) => survey.surveyPlannedEndDate ?
          this.dateFormatService.convertUTCDateStringToUserTimeZoneString(survey.surveyPlannedEndDate, this.dateFormatService.userDateFormat) : '-'
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.surveyStatus),
        displayName: 'Status',
        cellTemplate: (survey) => this.translateService.getDynamicTranslation(SurveyStatusLabels[survey.surveyStatus] ?? 'Unknown'),
        cssClass: (survey) => SurveyStatusCssClasses[survey.surveyStatus] ?? ''
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (sampleSurvey) => this.organizationLevels.find(res => res.id === sampleSurvey.organizationLevelId)?.name ?? '-'
      }),
      new ColumnConfig<SampleSurvey>({
        name: nameOf<SampleSurvey>(x => x.numberOfSamplesTaken),
        displayName: 'Number of Samples Taken',
        cellTemplate: (sampleSurvey) => (sampleSurvey.numberOfSamplesTaken).toString() ?? '-',
        cssClass: (sampleSurvey) => sampleSurvey.numberOfSamplesTaken ? 'cursor-pointer' : '',
        onClick: (survey) => this.openSampleGrid(survey)
      }),
      { name: 'action', displayName: 'Action' }
    ];
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<SampleSurvey>(x => x.organizationLevelId): return this.organizationLevelService;
      case nameOf<SampleSurvey>(x => x.surveyTypeId): return this.sampleSurveyTypeService;
      default: return undefined;
    }
  }

  openSampleSurveyDetail(item: SampleSurvey) {
    this.router.navigate([`/dashboard/survey-details/${item.id}`]);
  }

  openSampleGrid(item: SampleSurvey) {
    this.router.navigate([`/dashboard/samples/sample`],
      { queryParams: { additionalFilters: JSON.stringify({ sampleSurveyId: item.id.toString() }) } }
    );
  }

  get hasOrgFilter() {
    return true;
  }

  protected get objectId(): string {
    return RolePermissionObjects.Survey;
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override initializeTabs() {
    return sampleRouteConfig;
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Surveys';
    this.searchPlaceholder = 'Search Surveys';
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(surveyId?: string): void {
    const surveyModel: IFormDialogData = { id: surveyId };
    this.openDialog(SurveyDialogComponent, surveyModel, false, '920px');
  }

  override openEntityDeleteDialog(sampleSurvey: SampleSurvey): void {
    this.openDeleteDialog(DeleteDialog, sampleSurvey, this.gridService, 'SampleSurvey');
  }
}
